import axios from 'axios'
import { API_BASE_URL } from '../config/env'
import { clearAuthData } from './authStorage'
import { refreshAccessToken } from './auth'

const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
		// evita a pagina de interstitial do ngrok em respostas de API
		'ngrok-skip-browser-warning': 'true',
	},
	withCredentials: false,
	timeout: 30000,
})

api.interceptors.request.use((config) => {
	const token = localStorage.getItem('authToken')
	const tokenType = localStorage.getItem('tokenType') || 'Bearer'

	if (token) {
		config.headers.Authorization = `${tokenType} ${token}`
	}
	return config
})

// Em 401, tenta renovar o access token via refresh_token uma unica vez e repete
// a chamada original antes de forcar logout - chamadas concorrentes reaproveitam
// a mesma renovacao em andamento.
let refreshingPromise = null

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config

		if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
			originalRequest._retry = true

			if (!refreshingPromise) {
				refreshingPromise = refreshAccessToken().finally(() => {
					refreshingPromise = null
				})
			}

			const newToken = await refreshingPromise
			if (newToken) {
				return api(originalRequest)
			}
		}

		if (error.response?.status === 401) {
			clearAuthData()
			window.location.href = '/login'
		}
		return Promise.reject(error)
	}
)

// Extrai a mensagem de erro mais util possivel da resposta da API
const apiError = (error, fallback) => {
	const data = error.response?.data
	const detail =
		data?.message ||
		data?.detail ||
		data?.error_description ||
		data?.error ||
		(Array.isArray(data?.errors) && data.errors.map((e) => e.message || e).join(', '))

	if (detail) return new Error(detail)
	if (error.response?.status) {
		return new Error(`${fallback} (HTTP ${error.response.status})`)
	}
	return new Error(error.message || fallback)
}

// Monta a query string ignorando campos vazios
const toQuery = (params = {}) => {
	const search = new URLSearchParams()
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			search.append(key, value)
		}
	})
	const query = search.toString()
	return query ? `?${query}` : ''
}

/* ------------------------------------------------------------------ */
/* BackOffice - Contas                                                 */
/* ------------------------------------------------------------------ */

export const fetchAccounts = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/accounts${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao listar contas')
	}
}

// Saldo local x saldo na QITech — a API consulta a QITech conta a conta, na hora.
export const fetchAccountsBalances = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/accounts/balances${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao consultar saldos das contas')
	}
}

export const fetchAccountById = async (id) => {
	try {
		const response = await api.get(`/backoffice/accounts/${id}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar a conta')
	}
}

export const fetchAccountAuthClient = async (publicId) => {
	try {
		const response = await api.get(`/backoffice/accounts/${publicId}/auth-client`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar o auth client')
	}
}

export const fetchAuthScopes = async () => {
	try {
		const response = await api.get('/backoffice/accounts/auth-client/scopes')
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar os scopes')
	}
}

export const addAccountScopes = async (publicId, ids) => {
	try {
		const response = await api.post(
			`/backoffice/accounts/${publicId}/auth-client/scopes`,
			{ ids }
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao adicionar scopes')
	}
}

export const removeAccountScopes = async (publicId, ids) => {
	try {
		const response = await api.delete(
			`/backoffice/accounts/${publicId}/auth-client/scopes`,
			{ data: { ids } }
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao remover scopes')
	}
}

export const fetchBillingConfiguration = async (publicId) => {
	try {
		const response = await api.get(`/backoffice/accounts/${publicId}/billing-configuration`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar a configuracao de tarifas')
	}
}

export const updateBillingConfiguration = async (publicId, payload) => {
	try {
		const response = await api.put(`/backoffice/accounts/${publicId}/billing-configuration`, payload)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao atualizar a configuracao de tarifas')
	}
}

export const fetchPixLimits = async (publicId) => {
	try {
		const response = await api.get(`/backoffice/accounts/${publicId}/pix/limits`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar os limites Pix')
	}
}

export const requestPixLimitChange = async (publicId, payload) => {
	try {
		const response = await api.post(`/backoffice/accounts/${publicId}/pix/limits`, payload)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao solicitar a alteracao dos limites Pix')
	}
}

// Sincroniza (gera/recalcula) os snapshots de saldo diario da conta para um
// intervalo de datas - equivalente ao POST /accounts/daily-balances/generate
// do dominio da conta, mas para uma conta especifica escolhida no backoffice.
export const syncAccountDailyBalances = async (publicId, { startDate, endDate }) => {
	try {
		const response = await api.post(
			`/backoffice/accounts/${publicId}/daily-balances/generate${toQuery({ startDate, endDate })}`
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao sincronizar o saldo diario da conta')
	}
}

// Sincroniza o saldo do BaaS (QITech) nos postings da conta para um intervalo
// de datas: busca o extrato no BaaS e grava o saldo pos-transacao em cada
// posting correspondente. Retorna { updatedPostings }.
export const syncAccountBaasBalance = async (publicId, { startDate, endDate }) => {
	try {
		const response = await api.post(
			`/backoffice/accounts/${publicId}/balance/sync${toQuery({ startDate, endDate })}`
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao sincronizar o saldo BaaS da conta')
	}
}

// Reenvia para a QITech a baixa dos boletos cancelados da conta cujo updated_at
// esta no intervalo de datas. Nao altera os boletos locais.
// Retorna { total, cancelled, skipped, failed, failures: [{ boletoPublicId, message }] }.
export const cancelAccountBoletosOnQiTech = async (accountPublicId, { startDate, endDate }) => {
	try {
		const response = await api.post(
			`/backoffice/boletos/qitech/cancel${toQuery({ accountPublicId, startDate, endDate })}`
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao cancelar os boletos na QITech')
	}
}

// Remove os descontos dos boletos informados (public ids) na QITech, enviando discounts_data = null,
// e limpa o desconto local de cada boleto aceito.
// Retorna { total, updated, skipped, failed, failures: [{ boletoPublicId, message }] }.
export const removeAccountBoletoDiscountsOnQiTech = async (accountPublicId, boletoIds) => {
	try {
		const response = await api.post('/backoffice/boletos/qitech/discount/remove', {
			accountPublicId,
			boletoIds,
		})
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao remover os descontos dos boletos na QITech')
	}
}

const QITECH_CONSULT_PATHS = {
	'pix-transfer': 'pix-transfer',
	'ted-transfer': 'ted-transfer',
	'bill-payment': 'bill-payment',
	'pix-batch': 'pix-transfer/batch',
	'pix-schedule-batch': 'pix-transfer/schedule-batch',
	'pix-schedule': 'pix-transfer/schedule',
	'ted-batch': 'ted-transfer/batch',
	'bill-payment-batch': 'bill-payment/batch',
}

// qiTechKey e a chave da propria QITech (transacao, lote ou agendamento).
// direction (outgoing/incoming) so se aplica a PIX e TED.
export const consultTransactionOnQiTech = async (accountPublicId, type, qiTechKey, direction) => {
	const path = QITECH_CONSULT_PATHS[type]
	if (!path) throw new Error(`Tipo de consulta desconhecido: ${type}`)
	try {
		const response = await api.get(
			`/backoffice/accounts/${accountPublicId}/qitech/${path}/${encodeURIComponent(qiTechKey)}${toQuery({ direction })}`
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao consultar a transacao na QITech')
	}
}

export const fetchPixLimitsRequests = async (publicId, params = {}) => {
	try {
		const response = await api.get(`/backoffice/accounts/${publicId}/pix/limits-request${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao listar as solicitacoes de limite Pix')
	}
}

/* ------------------------------------------------------------------ */
/* BackOffice - Metricas                                               */
/* ------------------------------------------------------------------ */

export const fetchBackOfficeMetrics = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/metrics${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao carregar as metricas')
	}
}

// Receita float (rebate diario sobre o saldo total das contas x CDI). Nao
// aceita accountPublicId - sempre reflete a soma de todas as contas da plataforma.
export const fetchFloatRevenue = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/metrics/float-revenue${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao carregar a receita float')
	}
}

// Boletos "a vencer" agrupados por dia de vencimento no mes filtrado -
// aceita accountPublicId opcional (sem filtro = todas as contas).
export const fetchBoletoDueDateSummary = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/metrics/boletos-vencimento${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao carregar os boletos por vencimento')
	}
}

/* ------------------------------------------------------------------ */
/* BackOffice - Onboardings                                            */
/* ------------------------------------------------------------------ */

export const fetchOnboardings = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/onboardings${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao listar onboardings')
	}
}

export const fetchOnboardingPjById = async (id) => {
	try {
		const response = await api.get(`/backoffice/onboardings/pj/${id}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar o onboarding')
	}
}

export const fetchOnboardingPjQiTechAccountRequest = async (id) => {
	try {
		const response = await api.get(`/backoffice/onboardings/pj/${id}/qitech-account-request`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao consultar o pedido de conta na QI Tech')
	}
}

export const fetchOnboardingPfById = async (id) => {
	try {
		const response = await api.get(`/backoffice/onboardings/pf/${id}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar o onboarding')
	}
}

export const finalizeOnboardingPf = async (id) => {
	try {
		const response = await api.post(`/backoffice/onboardings/pf/${id}/finalize`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao finalizar o onboarding')
	}
}

/* ------------------------------------------------------------------ */
/* BackOffice - Pagadoria (paying agents)                              */
/* ------------------------------------------------------------------ */

export const fetchPayingAgents = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/paying-agents${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao listar as pagadorias')
	}
}

export const fetchPayingAgentById = async (publicId) => {
	try {
		const response = await api.get(`/backoffice/paying-agents/${publicId}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar a pagadoria')
	}
}

// Lista onboardings PJ abertos para virar pagadoria (em andamento ou ja
// finalizados) - unifica o que antes so dava pra ver conta por conta.
export const fetchPayingAgentOnboardings = async (params = {}) => {
	try {
		const response = await api.get(`/backoffice/paying-agents/onboardings${toQuery(params)}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao listar os onboardings de pagadoria')
	}
}

export const fetchPayingAgentOnboardingById = async (onboardingId) => {
	try {
		const response = await api.get(`/backoffice/paying-agents/onboardings/${onboardingId}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar o onboarding de pagadoria')
	}
}

export const createPayingAgent = async (payload) => {
	try {
		const response = await api.post('/backoffice/paying-agents', payload)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao criar a associacao de conta')
	}
}

// Abre um onboarding PJ com os dados da conta MASTER para criar a conta de
// pagadoria. A conta so nasce quando esse onboarding for finalizado (a biometria
// dos representantes legais precisa ser refeita).
export const createPayingAgentAccount = async (accountPublicId) => {
	try {
		const response = await api.post('/backoffice/paying-agents/accounts', { accountPublicId })
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao solicitar a conta de pagadoria')
	}
}

/* ------------------------------------------------------------------ */
/* Onboarding PJ (fluxo de criacao de conta)                           */
/* ------------------------------------------------------------------ */

// Gera (ou reaproveita) o link de biometria como efeito colateral de atualizar o
// socio - a rota direta de onboarding (/onboardings/pj/.../biometric-link) exige
// o scope onboardingpj.company.update, que o client "backoffice" nao tem.
export const generateBiometricLink = async (onboardingId, partnerOnboardingId, legalRepresentative) => {
	try {
		const response = await api.patch(
			`/backoffice/onboardings/pj/${onboardingId}/legal-representatives/${partnerOnboardingId}`,
			{ legalRepresentative }
		)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao gerar o link de biometria')
	}
}

// Finalizacao no escopo do backoffice (SCOPE_backoffice.onboarding.update), para
// o painel nao depender do scope de onboarding PJ.
export const finalizeOnboarding = async (onboardingId) => {
	try {
		const response = await api.post(`/backoffice/onboardings/pj/${onboardingId}/finalize`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao finalizar o onboarding')
	}
}

export const updatePayingAgent = async (publicId, active) => {
	try {
		const response = await api.put(`/backoffice/paying-agents/${publicId}`, { active })
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao atualizar a pagadoria')
	}
}

export const deletePayingAgent = async (publicId) => {
	try {
		const response = await api.delete(`/backoffice/paying-agents/${publicId}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao desativar a pagadoria')
	}
}

export const fetchPayingAgentBoleto = async (boletoId) => {
	try {
		const response = await api.get(`/paying-agent/boletos/${boletoId}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao consultar o boleto')
	}
}

/* ------------------------------------------------------------------ */
/* BackOffice - Utils                                                  */
/* ------------------------------------------------------------------ */

export const testQiTech = async () => {
	try {
		const response = await api.post('/backoffice/qitech/test')
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao testar a autenticacao QI Tech')
	}
}

export const populateBanks = async () => {
	try {
		const response = await api.post('/backoffice/banks/populate')
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao popular a lista de bancos')
	}
}

export const sendWebhook = async (payload) => {
	try {
		const response = await api.post('/webhooks', payload)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao enviar o webhook')
	}
}

/* ------------------------------------------------------------------ */
/* Planos (telas legadas, fora da collection BackOffice)               */
/* ------------------------------------------------------------------ */

export const fetchPlans = async (page = 1, pageSize = 10) => {
	try {
		const response = await api.get(`/backoffice/plans?page=${page}&pageSize=${pageSize}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao listar planos')
	}
}

export const fetchPlanById = async (id) => {
	try {
		const response = await api.get(`/backoffice/plans/${id}`)
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao buscar o plano')
	}
}

export const createPlan = async (formData) => {
	try {
		const response = await api.post('/backoffice/plans', formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao criar o plano')
	}
}

export const updatePlan = async (id, formData) => {
	try {
		const response = await api.patch(`/backoffice/plans/${id}`, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})
		return response.data
	} catch (error) {
		throw apiError(error, 'Falha ao atualizar o plano')
	}
}

export default api
